import type { DesignModuleSpecJson } from '../data/contracts';
import type { ModuleSpecBlock, ValidationIssue } from '../data/moduleSpecContracts';
import { evaluateFormula } from '@/uptk/core/components/dashboard/data/formula';
import { asRecord, asRecords, asString, cloneJson } from './specTypes';
export type EditableBlock = Omit<ModuleSpecBlock, 'measures'> & {
    measures?: (NonNullable<ModuleSpecBlock['measures']>[number] & {
        filter?: {
            field: string;
            operator: 'eq' | 'like' | 'gt' | 'lt';
            value: string;
        }[];
    })[];
};
export const NUMERIC_TYPES = ['numeric', 'integer', 'bigint', 'decimal', 'number', 'amount', 'percentage', 'int', 'long'];
/** Lecture seule : la forme historique n'est convertie que lors d'une modification explicite. */
export function readDashboard(spec: DesignModuleSpecJson): EditableBlock[] {
    return asRecords(asRecord(spec.dashboard)?.widgets).map((widget, index) => {
        if (typeof widget.type === 'string')
            return cloneJson(widget) as unknown as EditableBlock;
        return { key: asString(widget.key), type: widget.kind === 'count' || widget.kind === 'kpi' ? 'kpi' : 'custom', ...(widget.kind !== 'count' && widget.kind !== 'kpi' ? { reason: 'no_native_block' as const, description: asString(widget.description) } : {}), title: asRecord(widget.title) as Record<string, string> ?? { fr: asString(widget.title) },
            ...((widget.kind === 'count' || widget.kind === 'kpi') && widget.entity ? { measures: [{ key: 'total', op: 'count', entity: asString(widget.entity) }], value: { formula: 'total', format: { kind: 'number' as const } } } : {}),
            box: { x: 0, y: index * 4, w: 6, h: 4 }, mobile: { order: index } };
    });
}
export type DashboardAction = {
    type: 'dashboard.block.add';
    block: EditableBlock;
} | {
    type: 'dashboard.block.update';
    key: string;
    patch: Partial<EditableBlock>;
} | {
    type: 'dashboard.block.remove';
    key: string;
} | {
    type: 'dashboard.block.move';
    from: number;
    to: number;
} | {
    type: 'dashboard.block.resize';
    key: string;
    box: NonNullable<EditableBlock['box']>;
} | {
    type: 'dashboard.mobile.order';
    keys: string[];
} | {
    type: 'dashboard.layout.set';
    boxes: Record<string, NonNullable<EditableBlock['box']>>;
};
export function updateDashboard(spec: DesignModuleSpecJson, action: DashboardAction): DesignModuleSpecJson | null {
    const blocks = readDashboard(spec);
    const before = readDashboard(spec);
    const originals = asRecords(asRecord(spec.dashboard)?.widgets);
    const index = 'key' in action ? blocks.findIndex(b => b.key === action.key) : -1;
    if (action.type === 'dashboard.block.add') {
        if (blocks.length >= 60 || blocks.some(b => b.key === action.block.key))
            return null;
        blocks.push(cloneJson(action.block));
    }
    else if (action.type === 'dashboard.block.remove') {
        if (index < 0)
            return null;
        blocks.splice(index, 1);
    }
    else if (action.type === 'dashboard.block.update') {
        if (index < 0)
            return null;
        const next = { ...blocks[index], ...cloneJson(action.patch) };
        for (const key of Object.keys(action.patch))
            if (action.patch[key as keyof EditableBlock] === undefined)
                delete (next as unknown as Record<string, unknown>)[key];
        blocks[index] = next;
    }
    else if (action.type === 'dashboard.block.resize') {
        if (index < 0)
            return null;
        blocks[index].box = { ...action.box };
    }
    else if (action.type === 'dashboard.block.move') {
        if (!Number.isInteger(action.from) || !Number.isInteger(action.to) || action.from < 0 || action.to < 0 || action.from >= blocks.length || action.to >= blocks.length)
            return null;
        const [block] = blocks.splice(action.from, 1);
        blocks.splice(action.to, 0, block);
    }
    else if (action.type === 'dashboard.mobile.order') {
        if (action.keys.length !== blocks.length || new Set(action.keys).size !== blocks.length || action.keys.some(key => !blocks.some(b => b.key === key)))
            return null;
        blocks.forEach(b => { b.mobile = { ...b.mobile, order: action.keys.indexOf(b.key) }; });
    }
    else
        blocks.forEach(b => { if (action.boxes[b.key])
            b.box = { ...action.boxes[b.key] }; });
    if (blocks.some(b => !b.key.match(/^[a-z][a-z0-9-]*$/)) || new Set(blocks.map(b => b.key)).size !== blocks.length)
        return null;
    return { ...spec, specVersion: '1.3', dashboard: { ...asRecord(spec.dashboard), widgets: blocks.map(block => { const index = before.findIndex(previous => previous.key === block.key); return index >= 0 && !originals[index].type && JSON.stringify(block) === JSON.stringify(before[index]) ? originals[index] : block; }) } };
}
/** Chemins JSON Pointer identiques au validateur serveur ; aucune donnée distante. */
export function dashboardIssues(spec: DesignModuleSpecJson): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const blocks = readDashboard(spec);
    const entities = asRecord(spec.entities) ?? {};
    const add = (code: string, path: string) => issues.push({ code, path, message: code });
    const fields = (entity: string) => asRecords(asRecord(entities[entity])?.fields);
    blocks.forEach((block, i) => {
        const p = '/dashboard/widgets/' + i;
        if (!Object.values(block.title ?? {}).some(title => typeof title === 'string' && title.trim()))
            add('schema', p + '/title');
        for (const slot of ['value', 'caption'] as const) {
            const format = block[slot]?.format;
            if (format?.decimals !== undefined && (!Number.isInteger(format.decimals) || format.decimals < 0 || format.decimals > 4))
                add('schema', p + '/' + slot + '/format/decimals');
        }
        const values: Record<string, number | null> = {};
        if (block.bind && !entities[block.bind.entity])
            add('block.bind_entity', p + '/bind/entity');
        block.measures?.forEach((m, j) => {
            const mp = p + '/measures/' + j;
            if (m.key in values)
                add('block.measure_key_duplicate', mp + '/key');
            values[m.key] = 1;
            if (!entities[m.entity])
                add('block.measure_entity', mp + '/entity');
            if (m.op !== 'count' && !fields(m.entity).some(f => f.name === m.field && NUMERIC_TYPES.includes(asString(f.type))))
                add('block.measure_field', mp + '/field');
            (m.filters ?? m.filter)?.forEach((f, k) => { if (!fields(m.entity).some(v => v.name === f.field))
                add('block.measure_field', mp + '/filter/' + k + '/field'); });
        });
        for (const slot of ['value', 'caption'] as const)
            if (block[slot]?.formula) {
                try {
                    evaluateFormula(block[slot]!.formula!, values);
                }
                catch (e) {
                    add(e instanceof Error ? e.message : 'block.formula_syntax', p + '/' + slot + '/formula');
                }
            }
        if (block.chart) {
            const measure = block.measures?.find(m => m.key === block.chart?.measure);
            if (!measure || !fields(measure.entity).some(f => f.name === block.chart?.groupBy))
                add('block.group_field', p + '/chart/groupBy');
        }
        if (block.rows)
            for (const key of ['labelField', 'valueField', 'targetField'] as const) {
                if (!fields(block.bind?.entity ?? '').some(f => f.name === block.rows?.[key] && (key === 'labelField' || NUMERIC_TYPES.includes(asString(f.type)))))
                    add('block.measure_field', p + '/rows/' + key);
            }
        const box = block.box;
        if (box) {
            if (!Object.values(box).every(Number.isInteger) || box.x < 0 || box.y < 0 || box.w < 1 || box.h < 1 || box.h > 60 || box.x + box.w > 12)
                add('block.box_bounds', p + '/box');
            if (blocks.slice(0, i).some(b => b.box && box.x < b.box.x + b.box.w && box.x + box.w > b.box.x && box.y < b.box.y + b.box.h && box.y + box.h > b.box.y))
                add('block.box_overlap', p + '/box');
        }
    });
    return issues;
}
